import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { TRPC_CLIENT } from '@frontend/const/const.TRPC_CLIENT';

interface UserDirectoryUser {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
}

interface UseUserDirectoryResult {
  users: UserDirectoryUser[];
  getUserName: (userId: string) => string;
  getUserEmail: (userId: string) => string | undefined;
  isLoading: boolean;
  refetch: () => void;
}

export function useUserDirectory(): UseUserDirectoryResult {
  // Fetch user directory using TanStack Query
  const userDirectoryQuery = useQuery({
    ...TRPC_CLIENT.users.getUserDirectory.queryOptions(),
    staleTime: 5 * 60 * 1000, // 5 minutes - data rarely changes
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });

  // Create lookup functions using useMemo for performance
  const getUserName = useMemo(() => {
    return (userId: string): string => {
      const user = userDirectoryQuery.data?.find((u) => u.id === userId);
      return user?.name || userId; // Fallback to userId if not found
    };
  }, [userDirectoryQuery.data]);

  const getUserEmail = useMemo(() => {
    return (userId: string): string | undefined => {
      const user = userDirectoryQuery.data?.find((u) => u.id === userId);
      return user?.email;
    };
  }, [userDirectoryQuery.data]);

  return {
    users: userDirectoryQuery.data || [],
    getUserName,
    getUserEmail,
    isLoading: userDirectoryQuery.isLoading,
    refetch: userDirectoryQuery.refetch,
  };
}
